import React, { useState, useEffect } from 'react';


export enum CatalogCategoryEnum {
  PREVENTIVE = 'PREVENTIVOS',
  CORRECTIVE = 'CORRECTIVOS',
  INSURANCE_CLAIMS = 'SINIESTROS_SEGUROS',
  LEGAL_PROCEDURES = 'TRAMITES_GESTORIAS_LEGAL',
  TELEMETRY_EQUIPMENT = 'EQUIPAMIENTO_TELEMETRIA',
  CUSTOMER_SERVICE = 'ATN_CLIENTE',
}

interface Vehicle {
  folio_bien: string;
  marca: string;
  modelo: string;
  version: string;
  anio: string;
  numero_serie: string;
  placa: string;
  gps_economico: string;
  kilometraje: string;
  fecha_kilometraje: string;
  conductor_nombre: string;
  conductor_apellido_paterno: string;
  conductor_apellido_materno: string;
  conductor_email: string;
  conductor_telefono: string;
  label: string;
  value: string;
  clave_estatus: string;
}

interface RequestHistoryItem {
  folio: string;
  categoria: string;
  fecha_alta: string;
  clave_estatus: string;
  comentarios: string;
}

interface CatalogDetail {
  id: number;
  nombre: string;
}

interface CatalogSubcategory {
  id: number;
  nombre: string;
  detalles?: CatalogDetail[];
}

interface CatalogCategory {
  id: number;
  nombre: string;
  key_label: string;
  subcategorias?: CatalogSubcategory[];
}

interface LocationState {
  clave_estado: string;
  estado: string;
}

interface City {
  clave_ciudad: string;
  ciudad: string;
}

interface WorkshopItem {
  label: string;
  value: string;
}

interface ServiceRequestFormData {
  vehicleId: string;
  categoryId: string;
  categoryKey: string;
  subcategoryId: string;
  detailId: string;
  mileage: string;
  canDrive: string;
  driverName: string;
  driverLastName: string;
  driverSecondLastName: string;
  contactMethod: string;
  driverPhone: string;
  driverEmail: string;
  comments: string;
  scheduleAppt: boolean;
  apptDate: string;
  providerId: string;
  centerId: string;
  relatedRequestId: string;
  insuranceNumber: string;
  accidentStateId: string;
  accidentCityId: string;
  accidentLocation: string;
}

interface ServiceRequestFormProps {
  folioBienOriginal?: string;
  isAdmin: boolean;
}


const ServiceRequestForm: React.FC<ServiceRequestFormProps> = ({ folioBienOriginal, isAdmin }) => {

  const [vehicleSearchTerm, setVehicleSearchTerm] = useState<string>("");
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [vehicleSearchResults, setVehicleSearchResults] = useState<Vehicle[]>([]);
  const [requestHistory, setRequestHistory] = useState<RequestHistoryItem[]>([]);

  const [formData, setFormData] = useState<ServiceRequestFormData>({
    vehicleId: folioBienOriginal || "",
    categoryId: "",
    categoryKey: "",
    subcategoryId: "",
    detailId: "",
    mileage: "",
    canDrive: "",
    driverName: "",
    driverLastName: "",
    driverSecondLastName: "",
    contactMethod: "",
    driverPhone: "",
    driverEmail: "",
    comments: "",
    scheduleAppt: false,
    apptDate: "",
    providerId: "",
    centerId: "",
    relatedRequestId: "",
    insuranceNumber: "",
    accidentStateId: "",
    accidentCityId: "",
    accidentLocation: ""
  });

  const [catalog, setCatalog] = useState<CatalogCategory[]>([]);
  const [availableSubcategories, setAvailableSubcategories] = useState<CatalogSubcategory[]>([]);
  const [availableDetails, setAvailableDetails] = useState<CatalogDetail[]>([]);

  const [statesList, setStatesList] = useState<LocationState[]>([]);
  const [citiesList, setCitiesList] = useState<City[]>([]);

  const [workshopSearchTerm, setWorkshopSearchTerm] = useState<string>("");
  const [workshopSearchResults, setWorkshopSearchResults] = useState<WorkshopItem[]>([]);

  const [isLoadingCatalog, setIsLoadingCatalog] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [statesRes, categoriesRes] = await Promise.all([
          fetch('/sittApi/obtener_estados.php'),
          fetch('/sittApi/obtener_categorias.php')
        ]);

        const statesData = await statesRes.json();
        const categoriesData = await categoriesRes.json();

        if (statesData.status === 'success') setStatesList(statesData.data);
        if (categoriesData.status === 'success') setCatalog(categoriesData.data);
      } catch (error) {
        console.error("Error loading initial catalogs", error);
      } finally {
        setIsLoadingCatalog(false);
      }
    };

    fetchInitialData();

    if (folioBienOriginal) {
      fetchVehicleHistory(folioBienOriginal, "");
    }
  }, [folioBienOriginal]);

  useEffect(() => {
    if (!formData.accidentStateId) {
      setCitiesList([]);
      return;
    }

    const fetchCities = async () => {
      try {
        const response = await fetch('/api/catalogos/ciudades.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ accion: 'filtered', idEstado: formData.accidentStateId })
        });
        const result = await response.json();
        setCitiesList(result);
      } catch (error) {
        console.error("Error loading cities", error);
      }
    };
    fetchCities();
  }, [formData.accidentStateId]);

  useEffect(() => {
    if (!catalog.length || !formData.categoryId) return;

    const category = catalog.find(c => c.id.toString() === formData.categoryId);
    if (category) {
      setFormData(prev => ({
        ...prev,
        categoryKey: category.key_label || "",
        subcategoryId: "",
        detailId: ""
      }));
      setAvailableSubcategories(category.subcategorias || []);
    }
  }, [formData.categoryId, catalog]);

  useEffect(() => {
    const subcategory = availableSubcategories.find(s => s.id.toString() === formData.subcategoryId);
    setAvailableDetails(subcategory?.detalles || []);
    setFormData(prev => ({ ...prev, detailId: "" }));
  }, [formData.subcategoryId, availableSubcategories]);


  const handleVehicleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setVehicleSearchTerm(term);

    if (term.length < 4) {
      setVehicleSearchResults([]);
      return;
    }

    try {
      const response = await fetch('/api/bienes/bienes.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ accion: 'search', term })
      });
      const data = await response.json();
      setVehicleSearchResults(data);
    } catch (error) {
      console.error("Error searching vehicles", error);
    }
  };

  const handleSelectVehicle = (vehicle: Vehicle) => {
    if (vehicle.value === "0") return;
    if (vehicle.clave_estatus !== "ACTI") {
      alert("El bien no se encuentra activo. No se pueden generar solicitudes.");
      return;
    }

    setSelectedVehicle(vehicle);
    setVehicleSearchTerm(vehicle.label);
    setVehicleSearchResults([]);

    setFormData(prev => ({
      ...prev,
      vehicleId: vehicle.folio_bien,
      driverName: vehicle.conductor_nombre || "",
      driverLastName: vehicle.conductor_apellido_paterno || "",
      driverSecondLastName: vehicle.conductor_apellido_materno || "",
      driverEmail: vehicle.conductor_email || "",
      driverPhone: vehicle.conductor_telefono || ""
    }));

    fetchVehicleHistory(vehicle.value, formData.categoryId);
  };

  const handleClearVehicle = () => {
    setSelectedVehicle(null);
    setVehicleSearchTerm("");
    setVehicleSearchResults([]);
    setRequestHistory([]);
    setFormData(prev => ({ ...prev, vehicleId: "" }));
  };

  const fetchVehicleHistory = async (vehicleId: string, categoryId: string) => {
    try {
      const response = await fetch('/api/operacion/solicitudes.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          "accion": "filtered",
          "idBien": vehicleId,
          "idCategoria": categoryId,
          "registros": "999"
        })
      });
      const data = await response.json();
      setRequestHistory(data);
    } catch (error) {
      console.error("Error fetching vehicle history", error);
    }
  };

  const handleWorkshopSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setWorkshopSearchTerm(term);

    if (term.length < 3) {
      setWorkshopSearchResults([]);
      return;
    }

    try {
      const response = await fetch('/api/proveedores/centros.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ accion: 'search', term })
      });
      const data = await response.json();
      setWorkshopSearchResults(data);
    } catch (error) {
      console.error("Error searching workshops", error);
    }
  };

  const handleSelectWorkshop = (workshop: WorkshopItem) => {
    setWorkshopSearchTerm(workshop.label);
    setWorkshopSearchResults([]);
    setFormData(prev => ({ ...prev, providerId: workshop.value }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const isChecked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? isChecked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.vehicleId) {
      alert("Por favor selecciona un bien en el Paso 1.");
      return;
    }

    setIsSaving(true);

    const apiPayload = {
      folio_bien: formData.vehicleId,
      clave_categoria: formData.categoryId,
      key_label: catalog.find((catalogo) => catalogo.id = Number(formData.categoryId))?.key_label,
      clave_subcategoria: formData.subcategoryId,
      clave_detalle: formData.detailId,
      kilometraje: formData.mileage,
      puede_circular: formData.canDrive,
      conductor_nombre: formData.driverName,
      conductor_apellido_paterno: formData.driverLastName,
      conductor_apellido_materno: formData.driverSecondLastName,
      conductor_email: formData.driverEmail,
      conductor_telefono: formData.driverPhone,
      metodo_contacto: formData.contactMethod,
      comentarios: formData.comments,

      agendar: formData.scheduleAppt ? 1 : 0,
      fecha_cita: formData.apptDate,
      clave_persona_proveedor: formData.providerId,
      clave_centro: formData.centerId,

      clave_solicitud_relacionada: formData.relatedRequestId,
      clave_estado: formData.accidentStateId,
      clave_ciudad: formData.accidentCityId,
      siniestro_ubicacion: formData.accidentLocation,
      siniestro_numero_aseguradora: formData.insuranceNumber
    };

    try {
      const response = await fetch('/sittApi/crear_solicitud.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPayload)
      });
      const result = await response.json();

      if (result.status === 'success') {
        alert("¡Éxito! " + result.message);
        window.location.reload();
      } else {
        alert("Error: " + result.message);
      }
    } catch (error) {
      alert("Error de red al intentar guardar la solicitud.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingCatalog) return <div className="p-5 text-center"><h5>Cargando sistema SITT...</h5></div>;

  return (
    <div>
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-light">
          <h2 className="mb-0 fs-5">{selectedVehicle ? "1. Confirma los datos del bien" : "1. Selecciona un bien"}</h2>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3 border-end">
              <div className="form-group mb-4 position-relative">
                <label className="form-label">Buscar Unidad</label>
                <div className="input-group">
                  <span className="input-group-text"><i className="far fa-search"></i></span>
                  <input type="search" className="form-control" placeholder="Placas o serie..." value={vehicleSearchTerm} onChange={handleVehicleSearch} readOnly={!!selectedVehicle} />
                  <button className="btn btn-outline-secondary" type="button" onClick={handleClearVehicle}><i className="far fa-times"></i></button>
                </div>

                {vehicleSearchResults.length > 0 && (
                  <ul className="list-group position-absolute w-100 shadow" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                    {vehicleSearchResults.map((vehicle, idx) => (
                      <button key={idx} type="button" className="list-group-item list-group-item-action" onClick={() => handleSelectVehicle(vehicle)}>
                        {vehicle.label}
                      </button>
                    ))}
                  </ul>
                )}
              </div>

              {selectedVehicle && (
                <div className="bg-light p-3 rounded">
                  <div className="mb-2"><strong>Unidad:</strong> <br />{`${selectedVehicle.marca} ${selectedVehicle.modelo} ${selectedVehicle.version} ${selectedVehicle.anio}`}</div>
                  <div className="mb-2"><strong>Número de serie:</strong> <br />{selectedVehicle.numero_serie || "N/A"}</div>
                  <div className="mb-2"><strong>Placas:</strong> <br />{selectedVehicle.placa || "N/A"}</div>
                  <div><strong>Económico:</strong> <br />{selectedVehicle.gps_economico || "N/A"}</div>
                </div>
              )}
            </div>

            <div className="col-md-9">
              <strong>Solicitudes anteriores</strong>
              <div className="table-responsive mt-2">
                <table className="table table-striped table-hover border">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center">Folio</th>
                      <th className="text-center">Tipo</th>
                      <th className="text-center">Fecha</th>
                      <th className="text-center">Estatus</th>
                      <th>Comentarios</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestHistory.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-4 text-muted"><strong>El bien no tiene solicitudes anteriores.</strong></td></tr>
                    ) : (
                      requestHistory.map((hist, i) => (
                        <tr key={i}>
                          <td className="text-center">{hist.folio}</td>
                          <td className="text-center">{hist.categoria}</td>
                          <td className="text-center">{hist.fecha_alta}</td>
                          <td className="text-center">
                            <span className={`badge bg-${hist.clave_estatus === 'ASIG' ? 'primary' : hist.clave_estatus === 'ATEN' ? 'success' : 'secondary'}`}>
                              {hist.clave_estatus}
                            </span>
                          </td>
                          <td>{hist.comentarios}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm">
          <div className="card-header bg-light">
            <h2 className="mb-0 fs-5">2. Llena los datos de la solicitud</h2>
          </div>
          <div className="card-body">
            <div className="row">

              <div className="col-md-3 border-end">
                <h3 className="fs-5 text-muted mb-3">Datos generales</h3>
                <div className="form-group mb-3">
                  <label className="fw-bold">Categoría Principal</label>
                  <select className="form-select" name="categoryId" value={formData.categoryId} onChange={handleInputChange} required disabled={!selectedVehicle}>
                    <option value="">Elegir...</option>
                    {catalog.map(cat => <option key={cat.id} value={cat.id}>{cat.nombre}</option>)}
                  </select>
                </div>

                <div className="form-group mb-3">
                  <label>Puede circular</label>
                  <select className="form-select" name="canDrive" value={formData.canDrive} onChange={handleInputChange} required disabled={!selectedVehicle}>
                    <option value="">Elegir...</option>
                    <option value="1">Sí</option>
                    <option value="0">No</option>
                  </select>
                </div>
                <div className="form-group mb-3">
                  <label>Kilometraje actual</label>
                  <input type="number" className="form-control" name="mileage" value={formData.mileage} onChange={handleInputChange} required disabled={!selectedVehicle} />
                  {selectedVehicle && (<span className='help-text'>Kilometraje en sistema {parseFloat(selectedVehicle.kilometraje).toFixed(2)} kms, última actualización: {selectedVehicle.fecha_kilometraje}</span>)}
                </div>
                <div className="form-group mb-3">
                  <label>Descripción detallada</label>
                  <textarea className="form-control" name="comments" value={formData.comments} onChange={handleInputChange} rows={3} required disabled={!selectedVehicle}></textarea>
                </div>
              </div>

              <div className="col-md-3 border-end">
                <h3 className="fs-5 text-muted mb-3">Datos del Conductor</h3>
                <div className="form-group mb-2"><label>Nombre</label><input type="text" className="form-control form-control-sm" name="driverName" value={formData.driverName} onChange={handleInputChange} required disabled={!selectedVehicle} /></div>
                <div className="form-group mb-2"><label>Apellido paterno</label><input type="text" className="form-control form-control-sm" name="driverLastName" value={formData.driverLastName} onChange={handleInputChange} required disabled={!selectedVehicle} /></div>
                <div className="form-group mb-3"><label>Apellido materno</label><input type="text" className="form-control form-control-sm" name="driverSecondLastName" value={formData.driverSecondLastName} onChange={handleInputChange} disabled={!selectedVehicle} /></div>

                <div className="form-group mb-2">
                  <label>Método de contacto</label>
                  <select className="form-select form-select-sm" name="contactMethod" value={formData.contactMethod} onChange={handleInputChange} required disabled={!selectedVehicle}>
                    <option value="">Elegir...</option>
                    <option value="1">Celular</option>
                    <option value="2">Correo electrónico</option>
                  </select>
                </div>

                {formData.contactMethod === "1" && (
                  <div className="form-group"><label>Celular</label><input className="form-control form-control-sm" type="text" name="driverPhone" value={formData.driverPhone} onChange={handleInputChange} required /></div>
                )}
                {formData.contactMethod === "2" && (
                  <div className="form-group"><label>Correo</label><input className="form-control form-control-sm" type="email" name="driverEmail" value={formData.driverEmail} onChange={handleInputChange} required /></div>
                )}
              </div>

              {(isAdmin && formData.categoryKey && [CatalogCategoryEnum.CORRECTIVE, CatalogCategoryEnum.PREVENTIVE, CatalogCategoryEnum.TELEMETRY_EQUIPMENT].includes(formData.categoryKey as CatalogCategoryEnum)) && (
                <div className="col-md-6">
                  <h3 className="fs-5 text-muted mb-3">Datos de cita</h3>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-check form-switch mt-4">
                        <input className="form-check-input" type="checkbox" role="switch" name="scheduleAppt" checked={formData.scheduleAppt} onChange={handleInputChange} id="switchAgendar" disabled={!selectedVehicle} />
                        <label className="form-check-label" htmlFor="switchAgendar">&nbsp;Agendar cita</label>
                      </div>
                    </div>
                    <div className="col-md-8">
                      {formData.scheduleAppt && (
                        <div className="form-group mb-3">
                          <label>Fecha y hora de cita</label>
                          <input type="datetime-local" className="form-control" name="apptDate" value={formData.apptDate} onChange={handleInputChange} required />
                        </div>
                      )}
                    </div>
                  </div>

                  {formData.scheduleAppt && (
                    <div className="form-group position-relative">
                      <label>Taller / Centro de servicio</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="far fa-search"></i></span>
                        <input type="search" className="form-control" placeholder="Buscar proveedor..." value={workshopSearchTerm} onChange={handleWorkshopSearch} />
                      </div>
                      {workshopSearchResults.length > 0 && (
                        <ul className="list-group position-absolute w-100 shadow" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                          {workshopSearchResults.map((t, idx) => (
                            <button key={idx} type="button" className="list-group-item list-group-item-action" onClick={() => handleSelectWorkshop(t)}>
                              {t.label}
                            </button>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
              {formData.categoryKey}
              {CatalogCategoryEnum.INSURANCE_CLAIMS}
              {isAdmin}
              {(isAdmin && formData.categoryKey === CatalogCategoryEnum.INSURANCE_CLAIMS) && (
                <div className="col-md-6 form_solicitud_circular">
                  <h3 className="fs-5 text-muted mb-3">Datos de siniestro</h3>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="accidentStateId">Estado</label>
                        <select className="form-select" name="accidentStateId" id="accidentStateId" value={formData.accidentStateId} onChange={handleInputChange} required>
                          <option value="">Elegir...</option>
                          {statesList.map(state => <option key={state.clave_estado} value={state.clave_estado}>{state.estado}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="accidentCityId">Ciudad</label>
                        <select className="form-select" name="accidentCityId" id="accidentCityId" value={formData.accidentCityId} onChange={handleInputChange} required>
                          <option value="">Elegir un estado...</option>
                          {citiesList.map(city => <option key={city.clave_ciudad} value={city.clave_ciudad}>{city.ciudad}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mt-2">
                        <label htmlFor="accidentLocation">Dirección / Detalles de ubicación</label>
                        <input type="text" className="form-control" name="accidentLocation" id="accidentLocation" value={formData.accidentLocation} onChange={handleInputChange} required />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mt-2">
                        <label htmlFor="insuranceNumber">Número aseguradora</label>
                        <input type="text" className="form-control" name="insuranceNumber" id="insuranceNumber" value={formData.insuranceNumber} onChange={handleInputChange} required />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card-footer bg-light text-end">
            <button type="submit" className="btn btn-success px-5" disabled={isSaving || !selectedVehicle}>
              {isSaving ? "Enviando..." : "Crear Solicitud de Servicio"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ServiceRequestForm;